import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  TextField,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  InsertDriveFile,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFiles } from '../context/FileContext';
import Notifications from './Notifications';
import '../styles/styles.css';

const FileUpload = () => {
  const navigate = useNavigate();
  const { addFile, replaceFile, maxFileSize } = useFiles();

  const [file, setFile] = useState(null);
  const [urlPath, setUrlPath] = useState('/');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [existingFileData, setExistingFileData] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const notificationsRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);
  const handleFileSelect = (e) => validateAndSetFile(e.target.files[0]);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.size > maxFileSize) {
      setError('File exceeds the size limit.');
      return;
    }
    setFile(selectedFile);
    setError('');
    setUploadComplete(false);
  };

  const removeFile = () => {
    setFile(null);
    setUploadComplete(false);
    setError('');
    setProgress(0);
  };

  const validateForm = () => {
    if (!file) { setError('Please select a file to upload.'); return false; }
    if (!urlPath.trim()) { setError('Please enter a path for the file.'); return false; }
    if (!urlPath.startsWith('/')) { setError('Path must start with "/".'); return false; }
    return true;
  };

  const startFakeProgress = () => {
    setProgress(15);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 85) { clearInterval(interval); return prev; }
        return prev + 10;
      });
    }, 120);
    return interval;
  };

  const handleUploadSuccess = () => {
    setProgress(100);
    setUploadComplete(true);
    notificationsRef.current?.showSnackbar('File saved successfully.', 'success');
    setTimeout(() => {
      setFile(null);
      setUrlPath('/');
      setProgress(0);
      setUploadComplete(false);
      navigate('/files');
    }, 1200);
  };

  const handleUpload = async (replace = false) => {
    if (!validateForm()) return;
    setUploading(true);
    setError('');
    const progressInterval = startFakeProgress();
    try {
      const result = replace
        ? await replaceFile({ file, urlPath }, existingFileData?.id)
        : await addFile({ file, urlPath });
      clearInterval(progressInterval);
      if (result.success) {
        handleUploadSuccess();
      } else if (result.error === 'FILE_EXISTS') {
        setExistingFileData(result.existingFile);
        setOpenConfirmDialog(true);
        setProgress(0);
      } else {
        throw new Error(result.error || 'Upload failed.');
      }
    } catch (err) {
      clearInterval(progressInterval);
      setProgress(0);
      setError(err.message);
      notificationsRef.current?.showSnackbar(err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmReplace = async () => {
    setOpenConfirmDialog(false);
    await handleUpload(true);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Box sx={{
      minHeight: '100svh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 60%, #f0eeff 100%)',
      padding: '80px 24px 24px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <Container maxWidth="sm" disableGutters>
        <Box sx={{
          background: '#ffffff', borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(108,99,255,0.12)',
          p: { xs: '24px 20px', sm: '36px 32px' },
          width: '100%', boxSizing: 'border-box',
        }}>
          <Typography sx={{
            fontSize: '2rem', fontWeight: 800, color: '#6C63FF',
            fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.02em',
            mb: 2, textAlign: 'center',
          }}>
            Upload
          </Typography>

          <input type="file" id="file-input" hidden onChange={handleFileSelect} />

          <label htmlFor="file-input">
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: `2px dashed ${dragOver ? '#4B45B3' : 'rgba(108,99,255,0.3)'}`,
                borderRadius: '14px',
                p: { xs: '24px 16px', sm: '36px 20px' },
                backgroundColor: dragOver ? 'rgba(108,99,255,0.06)' : 'rgba(108,99,255,0.02)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                mb: 2,
                '&:hover': { backgroundColor: 'rgba(108,99,255,0.05)', borderColor: '#6C63FF' },
              }}
            >
              <Box sx={{
                width: 48, height: 48, borderRadius: '12px',
                background: 'rgba(108,99,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 10px',
              }}>
                <CloudUpload sx={{ fontSize: 24, color: '#6C63FF' }} />
              </Box>
              <Typography sx={{ fontWeight: 600, color: '#6C63FF', fontSize: '0.95rem', mb: 0.5 }}>
                {file ? 'File selected' : 'Drop it here'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.85rem' }}>or tap to browse</Typography>
            </Box>
          </label>

          {file && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              background: 'rgba(108,99,255,0.06)', borderRadius: '12px',
              p: '10px 14px', mb: 2,
            }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '8px',
                background: 'rgba(108,99,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {uploadComplete
                  ? <CheckCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                  : <InsertDriveFile sx={{ fontSize: 18, color: '#6C63FF' }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#718096' }}>{formatBytes(file.size)}</Typography>
              </Box>
              <IconButton size="small" onClick={removeFile} sx={{ color: '#aaa' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ position: 'relative', height: 6, borderRadius: 3, backgroundColor: 'rgba(108,99,255,0.12)', overflow: 'hidden' }}>
                <Box sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${progress}%`, borderRadius: 3,
                  backgroundColor: '#6C63FF', transition: 'width 0.15s ease',
                }} />
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            variant="outlined"
            label="URL Path"
            value={urlPath}
            onChange={(e) => setUrlPath(e.target.value)}
            placeholder="/documents"
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '& fieldset': { borderColor: 'rgba(108,99,255,0.2)' },
                '&:hover fieldset': { borderColor: '#6C63FF' },
                '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#6C63FF' },
            }}
          />

          {error && (
            <Typography sx={{ color: '#e53935', fontSize: '0.8rem', mb: 1.5 }}>{error}</Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => handleUpload(false)}
            disabled={!file || !urlPath || uploading}
            startIcon={<CloudUpload />}
            sx={{
              backgroundColor: '#6C63FF', borderRadius: '12px',
              textTransform: 'none', fontWeight: 600, py: 1.2,
              fontSize: '0.95rem', boxShadow: 'none',
              '&:hover': { backgroundColor: '#4B45B3', boxShadow: 'none' },
              '&:disabled': { backgroundColor: 'rgba(108,99,255,0.2)', color: '#fff' },
            }}
          >
            {uploading ? 'Saving...' : 'Save File'}
          </Button>
        </Box>
      </Container>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Replace existing file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A file named "{existingFileData?.name}" already exists at this path. Do you want to replace it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={handleConfirmReplace} variant="contained" sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#6C63FF', '&:hover': { backgroundColor: '#4B45B3' } }}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      <Notifications ref={notificationsRef} />
    </Box>
  );
};

export default FileUpload;